import type { JsonSchemaNode, LpsConnector } from './lps.api';

/**
 * Turns a connector's config JSON schema (as LPS publishes it) into form
 * fields, and form values back into a config object. Pure on purpose: the
 * dialog stays a thin renderer, and a new connector needs no UI code.
 */

export type ConfigFieldKind = 'text' | 'secret' | 'number' | 'boolean' | 'enum' | 'list' | 'json';

export interface ConfigField {
  key: string;
  label: string;
  kind: ConfigFieldKind;
  required: boolean;
  options?: ReadonlyArray<string>;
  hint?: string;
}

export type ConfigFormValues = Record<string, string | boolean>;

const LABELS: Record<string, string> = {
  formName: 'Form name',
  signingSecret: 'Signing secret',
  fieldMap: 'Field mapping',
  destinationByForm: 'Destination by form name',
  verifyToken: 'Verify token',
  appSecret: 'App secret',
  pageAccessToken: 'Page access token',
  graphVersion: 'Graph API version',
  provider: 'Provider',
  keywords: 'Keywords',
  minMessageLength: 'Minimum message length',
  landingPageName: 'Landing page name',
  landingPageUrl: 'Landing page URL',
  defaultUtmSource: 'Default utm_source',
  defaultUtmCampaign: 'Default utm_campaign',
};

const HINTS: Record<string, string> = {
  formName: 'Shown on every lead from this connection.',
  signingSecret: 'Optional. When set, the sender must sign each request (x-lps-signature).',
  fieldMap:
    'Optional JSON, only when the form uses unusual field names. Example: {"phone": "fields.mobile"}',
  verifyToken: 'Any string you choose. Paste the same value in the webhook setup on Meta.',
  appSecret: 'From the Meta app settings. Used to check that deliveries really come from Meta.',
  pageAccessToken: 'Needed to fetch the answers of each lead from Meta.',
  keywords: 'Optional, comma separated. Only messages containing one of these become leads.',
  minMessageLength: 'Ignore very short replies like "ok".',
  landingPageUrl: 'Used when the page does not send its own URL.',
  defaultUtmSource: 'Used when the page does not send UTMs.',
  defaultUtmCampaign: 'Used when the page does not send UTMs.',
};

/**
 * Hints that differ by connector. Meta's field map reads the other way round:
 * the key is Meta's question, the value is our field.
 */
const CONNECTOR_HINTS: Record<string, Record<string, string>> = {
  'meta-lead-ads': {
    fieldMap:
      'Optional JSON: Meta question key -> our field (destination, travelDate, budget, fullName, phone, email, message). Example: {"where_would_you_like_to_travel?": "destination"}',
    destinationByForm:
      'Optional JSON, for forms that do not ask the destination. Text in the form, campaign or ad set name -> CRM destination. Names like "Kashmir_OTP" or "Hunar_Vietnam-OTP" already work without this. Example: {"EastEurope": "Europe", "Andaman": "Andaman and Nicobar Islands"}',
    pageAccessToken:
      'System-user token with leads_retrieval. Needed to fetch the answers of each lead from Meta.',
  },
  'website-form': {
    fieldMap:
      'Optional JSON: our field -> the name the form posts. Only for unusual names. Example: {"phone": "mobile_no"}',
  },
  'landing-page': {
    fieldMap:
      'Optional JSON: our field -> the name the page posts. Example: {"destination": "trip_to", "phone": "whatsapp_number"}',
  },
};

const humanise = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase())
    .replace(/ ([A-Z])/g, (m) => m.toLowerCase());

const kindOf = (node: JsonSchemaNode, isSecret: boolean): ConfigFieldKind => {
  if (node.enum && node.enum.length > 0) return 'enum';
  switch (node.type) {
    case 'boolean':
      return 'boolean';
    case 'integer':
    case 'number':
      return 'number';
    case 'array':
      return 'list';
    case 'object':
      return 'json';
    default:
      return isSecret ? 'secret' : 'text';
  }
};

export const describeConfig = (connector: LpsConnector): ConfigField[] => {
  const props = connector.configSchema.properties ?? {};
  const required = new Set(connector.configSchema.required ?? []);
  return Object.entries(props).map(([key, node]) => ({
    key,
    label: LABELS[key] ?? humanise(key),
    kind: kindOf(node, connector.secretFields.includes(key)),
    // Defaults make a field optional for the person filling the form.
    required: required.has(key) && node.default === undefined,
    ...(node.enum ? { options: node.enum } : {}),
    ...((CONNECTOR_HINTS[connector.id]?.[key] ?? HINTS[key])
      ? { hint: CONNECTOR_HINTS[connector.id]?.[key] ?? HINTS[key] }
      : {}),
  }));
};

/** Seed the form from a saved config (secrets arrive masked) or from schema defaults. */
export const toFormValues = (
  connector: LpsConnector,
  fields: ReadonlyArray<ConfigField>,
  config: Record<string, unknown> | null,
): ConfigFormValues => {
  const values: ConfigFormValues = {};
  for (const f of fields) {
    const node = connector.configSchema.properties?.[f.key] ?? {};
    const v = config ? config[f.key] : node.default;
    if (f.kind === 'boolean') values[f.key] = v === true;
    else if (f.kind === 'list') values[f.key] = Array.isArray(v) ? v.join(', ') : '';
    else if (f.kind === 'json') {
      const isEmpty =
        v === undefined || v === null || (typeof v === 'object' && Object.keys(v).length === 0);
      values[f.key] = isEmpty ? '' : JSON.stringify(v, null, 2);
    } else values[f.key] = v === undefined || v === null ? '' : String(v);
  }
  return values;
};

/**
 * Build the config to send. Empty inputs are left out, so on an update the
 * stored value stays (LPS merges), and a masked secret sent back unchanged is
 * kept by LPS as well.
 */
export const toConfig = (
  fields: ReadonlyArray<ConfigField>,
  values: ConfigFormValues,
  mode: 'create' | 'edit',
): { config: Record<string, unknown>; errors: Record<string, string> } => {
  const config: Record<string, unknown> = {};
  const errors: Record<string, string> = {};

  for (const f of fields) {
    const raw = values[f.key];
    if (f.kind === 'boolean') {
      config[f.key] = raw === true;
      continue;
    }
    const text = typeof raw === 'string' ? raw.trim() : '';
    if (!text) {
      if (f.required && mode === 'create') errors[f.key] = `${f.label} is required`;
      continue;
    }
    switch (f.kind) {
      case 'number': {
        const n = Number(text);
        if (!Number.isFinite(n)) errors[f.key] = `${f.label} must be a number`;
        else config[f.key] = n;
        break;
      }
      case 'list':
        config[f.key] = text
          .split(/[,\n]/)
          .map((s) => s.trim())
          .filter(Boolean);
        break;
      case 'json':
        try {
          const parsed: unknown = JSON.parse(text);
          if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            errors[f.key] = `${f.label} must be a JSON object`;
          } else config[f.key] = parsed;
        } catch {
          errors[f.key] = `${f.label} is not valid JSON`;
        }
        break;
      default:
        config[f.key] = text;
    }
  }
  return { config, errors };
};
